import { Suspense } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Download, Pencil } from '@factorialco/f0-react/icons/app'
import {
  ProjectForSelectFragment,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
  SubprojectDataCollectionItemFragment,
  SubprojectsDataCollectionDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import {
  getStatusColor,
  getStatusLabel,
} from 'modules/project_management/components/labelTags/StatusTag'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

interface SubprojectsDataCollectionProps {
  projectId: number
  canExportData: boolean
  canManageSubprojects: boolean
  canSeeCosts: boolean
  projectCurrency: string
  hasSubprojectsEntityFeature: boolean
  onClick: (newData: ISubprojectsPanelData) => void
  budgetStrategyDelegation: boolean
  budgetStrategyBudgetType: ProjectManagementValueObjectsBudgetStrategyTypeEnum | null
  billableProject?: boolean
}

const SubprojectsDataCollection = ({
  projectId,
  canExportData,
  canManageSubprojects,
  canSeeCosts,
  projectCurrency,
  hasSubprojectsEntityFeature,
  onClick,
  budgetStrategyDelegation,
  budgetStrategyBudgetType,
  billableProject,
}: SubprojectsDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const itemOnClick = (item: SubprojectDataCollectionItemFragment) => () => {
    onClick({
      inputedMinutes: item.inputedMinutes ?? 0,
      laborCostCents:
        (item.laborCostCents ?? 0) + (item.fixedCostCents ?? 0) + (item.spendingCostsCents ?? 0),
      subprojectId: item.id ?? 0,
      subprojectName: item.name ?? '',
    })
  }

  const calculateCostConversion = (cost: number | null | undefined) => {
    return MoneyWithCurrency({
      optionalDecimals: true,
      amount: cost ?? 0,
      currency: projectCurrency,
      locale,
    })
  }

  const calculateTotalCost = (item: SubprojectDataCollectionItemFragment) =>
    (item.laborCostCents ?? 0) + (item.fixedCostCents ?? 0) + (item.spendingCostsCents ?? 0)

  const calculateProfitMargin = (item: SubprojectDataCollectionItemFragment) => {
    const totalCost = calculateTotalCost(item)
    const feeAmount = item.budgetStrategy?.feeAmountCents ?? 0
    const totalBudgetReached = feeAmount < totalCost

    return totalBudgetReached
      ? calculateCostConversion(feeAmount - totalCost)
      : calculateCostConversion((item.usedBudget ?? 0) - totalCost)
  }

  const hasBudget = budgetStrategyBudgetType === 'total_budget'
  const noBudget = budgetStrategyBudgetType === 'without_budget'
  const showMargins = hasSubprojectsEntityFeature && budgetStrategyDelegation && hasBudget

  const hoursColumn = () => {
    if (showMargins) {
      return {
        label: i18n.t('project_management.table.subprojects_list.headers.hours_margin'),
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${formatProjectHours(item.inputedMinutes)} / ${formatProjectHours(
            item.budgetStrategy?.plannedMinutes ?? 0
          )}`,
        }),
      }
    }

    return {
      label: i18n.t('project_management.table.projects_list.headers.hours'),
      render: (item: SubprojectDataCollectionItemFragment) => ({
        type: 'text' as const,
        value: formatProjectHours(item.inputedMinutes),
      }),
    }
  }

  const budgetColumn = () => {
    // Used budget (total_budget, no delegation, billable)
    if (hasSubprojectsEntityFeature && hasBudget && !budgetStrategyDelegation && billableProject) {
      return {
        label: i18n.t(
          'project_management.overview.sections.financial_details.insights.revenue_to_date.tooltip_message.used_budget'
        ),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${calculateCostConversion(item.usedBudget)}`,
        }),
      }
    }

    // Budget margin (total_budget, with delegation)
    if (showMargins) {
      return {
        label: i18n.t('project_management.table.subprojects_list.headers.budget_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${calculateCostConversion(item.usedBudget)} / ${calculateCostConversion(
            item.budgetStrategy?.feeAmountCents ?? 0
          )}`,
        }),
      }
    }

    // Billable to date (without_budget)
    if (hasSubprojectsEntityFeature && noBudget && (budgetStrategyDelegation || billableProject)) {
      return {
        label: i18n.t(
          'project_management.overview.sections.financial_details.insights.revenue_to_date.tooltip_message.billable_to_date'
        ),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${calculateCostConversion(item.usedBudget)}`,
        }),
      }
    }

    return null
  }

  const costColumn = () => {
    if (!canSeeCosts) return null

    // Cost margin
    if (hasSubprojectsEntityFeature && budgetStrategyDelegation && !noBudget) {
      return {
        label: i18n.t('project_management.table.subprojects_list.headers.cost_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${calculateCostConversion(calculateTotalCost(item))} / ${calculateCostConversion(
            item.budgetStrategy?.plannedCents ?? 0
          )}`,
        }),
      }
    }

    // Just cost
    return {
      label: hasSubprojectsEntityFeature
        ? i18n.t('project_management.table.subprojects_list.headers.cost')
        : i18n.t('project_management.table.subprojects_list.headers.labor_cost'),
      align: 'right' as const,
      render: (item: SubprojectDataCollectionItemFragment) => ({
        type: 'text' as const,
        value: hasSubprojectsEntityFeature
          ? calculateCostConversion(calculateTotalCost(item))
          : calculateCostConversion(item.laborCostCents ?? 0),
      }),
    }
  }

  const profitColumn = () => {
    if (!hasSubprojectsEntityFeature || !budgetStrategyDelegation) return null

    // Profit margin
    if (!noBudget) {
      return {
        label: i18n.t('project_management.table.subprojects_list.headers.profit_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: `${calculateProfitMargin(item)} / ${calculateCostConversion(
            (item.budgetStrategy?.feeAmountCents ?? 0) - (item.budgetStrategy?.plannedCents ?? 0)
          )}`,
        }),
      }
    }

    // Just profit
    return {
      label: i18n.t('project_management.table.subprojects_list.headers.profit'),
      align: 'right' as const,
      render: (item: SubprojectDataCollectionItemFragment) => ({
        type: 'text' as const,
        value: calculateCostConversion((item.usedBudget ?? 0) - (item.laborCostCents ?? 0)),
      }),
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: SubprojectsDataCollectionDocument,
    mapData: (data) => {
      return data.projectManagement.subprojectsConnection.nodes
    },
    mapVariables: ({ search, sortings }) => ({
      projectIds: [projectId],
      includeCost: canSeeCosts,
      hasSubprojectsEntity: hasSubprojectsEntityFeature,
      name: search,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.subprojectsConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.subprojects.total_items', { count: totals })
    },
    itemOnClick: itemOnClick,
    itemActions: canManageSubprojects
      ? (item) => {
          if (item.id !== null) {
            return compact([
              !hasSubprojectsEntityFeature
                ? {
                    label: i18n.t('project_management.table.subprojects_list.menu_links.edit_name'),
                    icon: Pencil,
                    onClick: () => {
                      track(trackingEvents.projectManagement.subprojects.editSubprojectStart, {
                        project_id: projectId,
                      })

                      navigate(
                        navigation.projects.id.details.subprojects.subprojectId.edit.fullPath({
                          id: projectId,
                          subprojectId: Number(item.id),
                        }),
                        { state: { subprojectName: item.name } }
                      )
                    },
                  }
                : undefined,
              {
                label: i18n.t(
                  'project_management.table.subprojects_list.menu_links.delete_subproject'
                ),
                icon: Delete,
                critical: true,
                onClick: () => {
                  track(trackingEvents.projectManagement.subprojects.deleteSubprojectStart, {
                    project_id: projectId,
                  })

                  navigate(
                    navigation.projects.id.details.subprojects.subprojectId.delete.fullPath({
                      id: projectId,
                      subprojectId: Number(item.id),
                    }),
                    { state: { subprojectName: item.name } }
                  )
                },
              },
            ])
          }

          return undefined
        }
      : undefined,
    primaryActions: () =>
      canManageSubprojects
        ? {
            label: i18n.t('project_management.subprojects.add_button'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.projectManagement.subprojects.addSubprojectStart, {
                project_id: Number(projectId),
              })

              navigate(
                navigation.projects.id.details.subprojects.create.fullPath({
                  id: Number(projectId),
                })
              )
            },
          }
        : undefined,
    secondaryActions: () =>
      canExportData
        ? [
            {
              label: i18n.t('project_management.table.projects_list.table_options.export.title'),
              description: i18n.t(
                'project_management.table.projects_list.table_options.export.description'
              ),
              icon: Download,
              onClick: () => {
                navigate(
                  navigation.projects.id.details.subprojects.export.fullPath({ id: projectId }),
                  { state: { projectForSelect: project } }
                )
              },
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
      },
    },
    defaultSortings: {
      field: 'name',
      order: 'asc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.name,
                  }),
                  sorting: 'name' as const,
                },
                hasSubprojectsEntityFeature && {
                  label: i18n.t('project_management.table.projects_list.headers.status'),
                  render: ({ status }: SubprojectDataCollectionItemFragment) => ({
                    type: 'status',
                    value: status
                      ? { label: getStatusLabel(status), status: getStatusColor(status) }
                      : undefined,
                  }),
                },
                hoursColumn(),
                budgetColumn(),
                costColumn(),
                profitColumn(),
                hasSubprojectsEntityFeature && {
                  label: i18n.t('project_management.table.projects_list.headers.startDate'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'date',
                    value: item.startDate ? new Date(item.startDate) : undefined,
                  }),
                },
                hasSubprojectsEntityFeature && {
                  label: i18n.t('project_management.table.projects_list.headers.dueDate'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'date',
                    value: item.dueDate ? new Date(item.dueDate) : undefined,
                  }),
                },
              ]),
            },
          },
        ]}
      />
      <Suspense>
        <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
      </Suspense>
    </>
  )
}

export default SubprojectsDataCollection
